<template>
	<div class="ks-sparkline" :style="containerStyle">
		<svg
			class="ks-sparkline-svg"
			:viewBox="'0 0 ' + chartWidth + ' ' + chartHeight"
			preserveAspectRatio="none"
		>
			<defs>
				<linearGradient
					v-if="hasGradient"
					:id="gradientId"
					:x1="gradientCoords.x1"
					:y1="gradientCoords.y1"
					:x2="gradientCoords.x2"
					:y2="gradientCoords.y2"
				>
					<stop
						v-for="(stopColor, idx) in gradientStops"
						:key="idx"
						:offset="gradientOffset(idx, gradientStops.length)"
						:stop-color="resolveColor(stopColor)"
					/>
				</linearGradient>
			</defs>

			<g v-if="points.length">
				<path
					v-if="showFilledTrend"
					:d="areaPath"
					:fill="areaFill"
					class="ks-sparkline-fill"
				/>

				<path
					v-if="isTrend"
					ref="linePath"
					:d="linePath"
					:stroke="strokeColor"
					:stroke-width="lineWidthNumber"
					stroke-linecap="round"
					stroke-linejoin="round"
					fill="none"
					class="ks-sparkline-line"
					:class="{ 'ks-sparkline-line-auto-draw': autoDraw }"
					:style="lineStyle"
				/>

				<g v-if="isBar" class="ks-sparkline-bars">
					<rect
						v-for="(point, idx) in points"
						:key="'bar-' + idx"
						:x="barRect(point, idx).x"
						:y="barRect(point, idx).y"
						:width="barRect(point, idx).width"
						:height="barRect(point, idx).height"
						:fill="strokeColor"
						rx="1"
					/>
				</g>
			</g>
		</svg>

		<div v-if="showLabels" class="ks-sparkline-labels" :style="labelStyle">
			<span
				v-for="(label, idx) in computedLabels"
				:key="'label-' + idx"
				class="ks-sparkline-label"
			>
				{{ label }}
			</span>
		</div>
	</div>
</template>

<script>
export default {
	name: 'KSparkline',
	props: {
		value: {
			type: Array,
			default: function () { return [] }
		},
		color: {
			type: String,
			default: 'accent'
		},
		gradient: {
			type: Array,
			default: function () { return [] }
		},
		gradientDirection: {
			type: String,
			default: 'top'
		},
		fill: {
			type: [Boolean, String],
			default: false
		},
		lineWidth: {
			type: [Number, String],
			default: 2
		},
		padding: {
			type: [Number, String],
			default: 8
		},
		smooth: {
			type: [Boolean, Number],
			default: false
		},
		autoDraw: {
			type: Boolean,
			default: false
		},
		autoDrawDuration: {
			type: [Number, String],
			default: 2000
		},
		autoDrawEasing: {
			type: String,
			default: 'ease'
		},
		labels: {
			type: Array,
			default: function () { return [] }
		},
		showLabels: {
			type: Boolean,
			default: false
		},
		labelSize: {
			type: [Number, String],
			default: 7
		},
		width: {
			type: [Number, String],
			default: 100
		},
		height: {
			type: [Number, String],
			default: 30
		},
		type: {
			type: String,
			default: 'trend'
		},
		min: {
			type: Number,
			default: null
		},
		max: {
			type: Number,
			default: null
		}
	},
	data () {
		return {
			pathLength: 0
		}
	},
	computed: {
		isTrend () {
			return this.type !== 'bar'
		},
		isBar () {
			return this.type === 'bar'
		},
		widthNumber () {
			return this.toFiniteNumber(this.width, 100)
		},
		heightNumber () {
			return this.toFiniteNumber(this.height, 30)
		},
		lineWidthNumber () {
			return Math.max(1, this.toFiniteNumber(this.lineWidth, 2))
		},
		paddingNumber () {
			return Math.max(0, this.toFiniteNumber(this.padding, 8))
		},
		chartWidth () {
			return Math.max(1, this.widthNumber)
		},
		chartHeight () {
			return Math.max(1, this.heightNumber)
		},
		containerStyle () {
			return {
				width: this.toCssSize(this.width),
				height: this.toCssSize(this.height)
			}
		},
		labelStyle () {
			return {
				fontSize: this.toCssSize(this.labelSize)
			}
		},
		normalizedValues () {
			return this.value
				.map((item) => Number(item))
				.filter((item) => isFinite(item))
		},
		minValue () {
			if (this.min !== null) return this.min
			if (!this.normalizedValues.length) return 0
			return Math.min.apply(null, this.normalizedValues)
		},
		maxValue () {
			if (this.max !== null) return this.max
			if (!this.normalizedValues.length) return 0
			return Math.max.apply(null, this.normalizedValues)
		},
		rangeValue () {
			var range = this.maxValue - this.minValue
			return range === 0 ? 1 : range
		},
		plotWidth () {
			return Math.max(1, this.chartWidth - this.paddingNumber * 2)
		},
		plotHeight () {
			return Math.max(1, this.chartHeight - this.paddingNumber * 2)
		},
		baseY () {
			return this.chartHeight - this.paddingNumber
		},
		points () {
			var values = this.normalizedValues
			if (!values.length) return []

			if (values.length === 1) {
				return [{
					x: this.paddingNumber + this.plotWidth / 2,
					y: this.valueToY(values[0]),
					value: values[0]
				}]
			}

			var step = this.plotWidth / (values.length - 1)
			return values.map((item, idx) => {
				return {
					x: this.paddingNumber + idx * step,
					y: this.valueToY(item),
					value: item
				}
			})
		},
		linePath () {
			if (!this.points.length) return ''
			if (this.points.length === 1) {
				var single = this.points[0]
				return 'M ' + single.x + ' ' + single.y + ' L ' + single.x + ' ' + single.y
			}

			if (!this.smooth) {
				return this.pointsToLinePath(this.points)
			}

			return this.pointsToSmoothPath(this.points)
		},
		areaPath () {
			if (!this.isTrend || !this.linePath) return ''
			var firstPoint = this.points[0]
			var lastPoint = this.points[this.points.length - 1]
			return this.linePath + ' L ' + lastPoint.x + ' ' + this.baseY + ' L ' + firstPoint.x + ' ' + this.baseY + ' Z'
		},
		hasGradient () {
			return this.gradientStops.length > 0
		},
		gradientStops () {
			if (!this.gradient || !this.gradient.length) return []
			return this.gradient
		},
		gradientId () {
			return 'ks-sparkline-gradient-' + this._uid
		},
		gradientCoords () {
			var dir = String(this.gradientDirection || '').toLowerCase()
			if (dir === 'bottom') return { x1: '0%', y1: '0%', x2: '0%', y2: '100%' }
			if (dir === 'left') return { x1: '100%', y1: '0%', x2: '0%', y2: '0%' }
			if (dir === 'right') return { x1: '0%', y1: '0%', x2: '100%', y2: '0%' }
			return { x1: '0%', y1: '100%', x2: '0%', y2: '0%' }
		},
		strokeColor () {
			if (this.hasGradient) return 'url(#' + this.gradientId + ')'
			return this.resolveColor(this.color)
		},
		areaFill () {
			if (typeof this.fill === 'string' && this.fill !== '') {
				return this.resolveColor(this.fill)
			}
			if (this.hasGradient) return 'url(#' + this.gradientId + ')'
			return this.resolveColor(this.color)
		},
		showFilledTrend () {
			return this.isTrend && !!this.fill && this.points.length > 1
		},
		lineStyle () {
			if (!this.autoDraw || !this.pathLength) return {}
			return {
				strokeDasharray: this.pathLength,
				strokeDashoffset: this.pathLength,
				animation: 'ks-sparkline-draw ' + this.toFiniteNumber(this.autoDrawDuration, 2000) + 'ms ' + this.autoDrawEasing + ' forwards'
			}
		},
		computedLabels () {
			if (this.labels.length) return this.labels
			return this.normalizedValues
		}
	},
	watch: {
		linePath () {
			this.refreshPathLength()
		},
		autoDraw () {
			this.refreshPathLength()
		}
	},
	mounted () {
		this.refreshPathLength()
	},
	updated () {
		if (this.autoDraw) {
			this.refreshPathLength()
		}
	},
	methods: {
		toFiniteNumber (value, fallback) {
			var num = Number(value)
			if (!isFinite(num)) return fallback
			return num
		},
		toCssSize (value) {
			if (typeof value === 'number') return value + 'px'
			if (String(value).match(/^\d+(\.\d+)?$/)) return value + 'px'
			return value
		},
		resolveColor (color) {
			var map = {
				accent: 'var(--ks-col-accent)',
				primary: 'var(--ks-col-primary)',
				secondary: 'var(--ks-col-secondary)',
				success: 'var(--ks-col-success)',
				danger: 'var(--ks-col-danger)',
				warning: 'var(--ks-col-warning)',
				info: 'var(--ks-col-info)',
				white: 'var(--ks-white)'
			}
			return map[color] || color
		},
		gradientOffset (index, total) {
			if (total <= 1) return '0%'
			return (index / (total - 1) * 100) + '%'
		},
		valueToY (value) {
			var ratio = (value - this.minValue) / this.rangeValue
			return this.paddingNumber + this.plotHeight - ratio * this.plotHeight
		},
		pointsToLinePath (points) {
			if (!points.length) return ''
			var path = 'M ' + points[0].x + ' ' + points[0].y
			for (var i = 1; i < points.length; i += 1) {
				path += ' L ' + points[i].x + ' ' + points[i].y
			}
			return path
		},
		pointsToSmoothPath (points) {
			if (points.length < 2) return this.pointsToLinePath(points)
			var smoothing = typeof this.smooth === 'number' ? this.smooth : 0.15
			var path = 'M ' + points[0].x + ' ' + points[0].y

			for (var i = 0; i < points.length - 1; i += 1) {
				var prev = points[i - 1] || points[i]
				var current = points[i]
				var next = points[i + 1]
				var after = points[i + 2] || next

				var cp1x = current.x + (next.x - prev.x) * smoothing
				var cp1y = current.y + (next.y - prev.y) * smoothing
				var cp2x = next.x - (after.x - current.x) * smoothing
				var cp2y = next.y - (after.y - current.y) * smoothing

				path += ' C ' + cp1x + ' ' + cp1y + ', ' + cp2x + ' ' + cp2y + ', ' + next.x + ' ' + next.y
			}

			return path
		},
		barRect (point, idx) {
			var count = this.points.length || 1
			var gap = Math.max(1, this.plotWidth / count)
			var width = Math.max(1, gap * 0.7)
			var x = this.paddingNumber + idx * gap + (gap - width) / 2
			var y = point.y
			var height = Math.max(1, this.baseY - y)
			return { x: x, y: y, width: width, height: height }
		},
		refreshPathLength () {
			if (!this.autoDraw || !this.$refs.linePath || !this.isTrend) {
				this.pathLength = 0
				return
			}
			this.$nextTick(() => {
				if (!this.$refs.linePath || typeof this.$refs.linePath.getTotalLength !== 'function') return
				this.pathLength = this.$refs.linePath.getTotalLength() || 0
			})
		}
	}
}
</script>

<style>
.ks-sparkline {
	display: inline-flex;
	flex-direction: column;
	justify-content: flex-start;
	color: var(--ks-font-color);
}

.ks-sparkline-svg {
	display: block;
	width: 100%;
	height: 100%;
	overflow: visible;
}

.ks-sparkline-line {
	transition: stroke 0.2s ease;
}

.ks-sparkline-fill {
	opacity: 0.22;
}

.ks-sparkline-labels {
	display: flex;
	justify-content: space-between;
	margin-top: 4px;
	color: var(--ks-font-color-accent);
	line-height: 1;
	white-space: nowrap;
	gap: 4px;
}

.ks-sparkline-label {
	flex: 1 1 auto;
	overflow: hidden;
	text-overflow: ellipsis;
}

@keyframes ks-sparkline-draw {
	to {
		stroke-dashoffset: 0;
	}
}
</style>
